import { useState, useEffect, useCallback, useRef } from "react";
import { useApi, useAuth } from "./AuthContext";
import {
  Users, Plus, Pencil, Trash2, Shield, User, X, Check,
  ScrollText, Filter, ChevronDown, ToggleLeft, ToggleRight,
  Eye, EyeOff, Edit3, Camera, Phone as PhoneIcon, IdCard, Heart,
  Briefcase, DollarSign
} from "lucide-react";
import { toast } from "sonner";
import { formatUSD } from "../utils/currency";

const ROLES = [
  { value: "admin", label: "Administrador Geral" },
  { value: "operador", label: "Operador" },
  { value: "operador_gerente", label: "Operador Gerente" },
  { value: "financeiro", label: "Financeiro" },
];

const MODULES = [
  { key: "clients", label: "Clients" },
  { key: "budgets", label: "Budgets" },
  { key: "service_orders", label: "Service Orders" },
  { key: "financial", label: "Financial" },
  { key: "leads", label: "Leads" },
  { key: "pricing", label: "Pricing" },
  { key: "portfolio", label: "Portfolio" },
  { key: "webmail", label: "Webmail" },
  { key: "expense_tracker", label: "Expense Tracker" },
  { key: "sales_pipeline", label: "Sales Pipeline (Comercial)" },
];

const emptyPerms = () => MODULES.reduce((acc, m) => {
  acc[m.key] = { view: false, edit: false };
  return acc;
}, {});

// ========== Permission Matrix Component ==========
function PermissionMatrix({ permissions, onChange, disabled }) {
  const toggle = (mod, field) => {
    if (disabled) return;
    const next = { ...permissions };
    next[mod] = { ...next[mod], [field]: !next[mod][field] };
    // If enabling edit, also enable view
    if (field === "edit" && next[mod].edit && !next[mod].view) {
      next[mod].view = true;
    }
    // If disabling view, also disable edit
    if (field === "view" && !next[mod].view && next[mod].edit) {
      next[mod].edit = false;
    }
    onChange(next);
  };

  return (
    <div className="bg-stone-50 rounded-xl border border-stone-200 overflow-hidden" data-testid="permission-matrix">
      <div className="grid grid-cols-3 gap-0 text-xs font-semibold text-stone-500 uppercase tracking-wider px-4 py-2.5 border-b border-stone-200 bg-stone-100">
        <span>Module</span>
        <span className="text-center flex items-center justify-center gap-1"><Eye className="w-3.5 h-3.5" /> View</span>
        <span className="text-center flex items-center justify-center gap-1"><Edit3 className="w-3.5 h-3.5" /> Edit</span>
      </div>
      {MODULES.map(m => (
        <div key={m.key} className="grid grid-cols-3 gap-0 px-4 py-2.5 border-b border-stone-100 last:border-b-0 items-center hover:bg-white transition-colors">
          <span className="text-sm font-medium text-stone-700">{m.label}</span>
          <div className="flex justify-center">
            <button
              type="button"
              data-testid={`perm-view-${m.key}`}
              onClick={() => toggle(m.key, "view")}
              disabled={disabled}
              className={`transition-colors ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
            >
              {permissions[m.key]?.view ? (
                <ToggleRight className="w-7 h-7 text-forest-600" />
              ) : (
                <ToggleLeft className="w-7 h-7 text-stone-300" />
              )}
            </button>
          </div>
          <div className="flex justify-center">
            <button
              type="button"
              data-testid={`perm-edit-${m.key}`}
              onClick={() => toggle(m.key, "edit")}
              disabled={disabled}
              className={`transition-colors ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
            >
              {permissions[m.key]?.edit ? (
                <ToggleRight className="w-7 h-7 text-forest-600" />
              ) : (
                <ToggleLeft className="w-7 h-7 text-stone-300" />
              )}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ========== Activity Logs Component ==========
function ActivityLogs() {
  const api = useApi();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [filterUser, setFilterUser] = useState("");
  const [filterModule, setFilterModule] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterUser) params.append("user_id", filterUser);
      if (filterModule) params.append("module", filterModule);
      if (filterDateFrom) params.append("date_from", filterDateFrom);
      if (filterDateTo) params.append("date_to", filterDateTo);
      const { data } = await api.get(`/admin/activity-logs?${params.toString()}`);
      setLogs(data);
    } catch (err) { toast.error("Failed to load logs"); }
    finally { setLoading(false); }
  }, [filterUser, filterModule, filterDateFrom, filterDateTo]);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/users"); setUsers(data); } catch (err) { console.warn("Failed to load users for filter:", err.message); }
    })();
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const formatDate = (ts) => {
    if (!ts) return "";
    const d = new Date(ts);
    return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: true });
  };

  const MODULE_COLORS = {
    users: "bg-amber-50 text-amber-700",
    permissions: "bg-purple-50 text-purple-700",
    clients: "bg-blue-50 text-blue-700",
    budgets: "bg-emerald-50 text-emerald-700",
    schedule: "bg-cyan-50 text-cyan-700",
    service_orders: "bg-indigo-50 text-indigo-700",
    financial: "bg-rose-50 text-rose-700",
    leads: "bg-orange-50 text-orange-700",
    pricing: "bg-stone-100 text-stone-700",
  };

  return (
    <div data-testid="activity-logs" className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ScrollText className="w-5 h-5 text-forest-600" />
          <h3 className="font-semibold text-stone-800">Activity Logs</h3>
        </div>
        <span className="text-sm text-stone-400">{logs.length} entries</span>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end bg-white rounded-xl border border-stone-200 p-4">
        <div>
          <label className="text-xs font-medium text-stone-500 mb-1 block">User</label>
          <select data-testid="log-filter-user" value={filterUser} onChange={e => setFilterUser(e.target.value)}
            className="px-3 py-1.5 border border-stone-300 rounded-lg text-sm">
            <option value="">All Users</option>
            {users.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-stone-500 mb-1 block">Module</label>
          <select data-testid="log-filter-module" value={filterModule} onChange={e => setFilterModule(e.target.value)}
            className="px-3 py-1.5 border border-stone-300 rounded-lg text-sm">
            <option value="">All Modules</option>
            {["users", "permissions", "clients", "budgets", "service_orders", "financial", "leads", "pricing"].map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-stone-500 mb-1 block">From</label>
          <input type="date" data-testid="log-filter-from" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)}
            className="px-3 py-1.5 border border-stone-300 rounded-lg text-sm" />
        </div>
        <div>
          <label className="text-xs font-medium text-stone-500 mb-1 block">To</label>
          <input type="date" data-testid="log-filter-to" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)}
            className="px-3 py-1.5 border border-stone-300 rounded-lg text-sm" />
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-white rounded-xl border border-stone-200 overflow-hidden shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center h-32 text-stone-500">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="logs-table">
              <thead>
                <tr className="border-b border-stone-200 bg-stone-50">
                  <th className="text-left px-4 py-3 font-medium text-stone-600">When</th>
                  <th className="text-left px-4 py-3 font-medium text-stone-600">Who</th>
                  <th className="text-left px-4 py-3 font-medium text-stone-600">Module</th>
                  <th className="text-left px-4 py-3 font-medium text-stone-600">Action</th>
                  <th className="text-left px-4 py-3 font-medium text-stone-600 hidden md:table-cell">Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-stone-400">No activity logs found</td></tr>
                ) : logs.map(log => (
                  <tr key={log.id} className="border-b border-stone-100 hover:bg-stone-50">
                    <td className="px-4 py-3 text-stone-500 whitespace-nowrap">{formatDate(log.timestamp)}</td>
                    <td className="px-4 py-3 font-medium text-stone-800">{log.user_name}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${MODULE_COLORS[log.module] || "bg-stone-100 text-stone-600"}`}>
                        {log.module}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-medium ${
                        log.action === "created" ? "text-green-700" :
                        log.action === "deleted" ? "text-red-600" : "text-blue-700"
                      }`}>{log.action}</span>
                    </td>
                    <td className="px-4 py-3 text-stone-600 hidden md:table-cell max-w-xs truncate" title={log.details}>
                      {log.details}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ========== Service Catalog Panel (admin-only CRUD) ==========
function ServiceCatalogPanel() {
  const api = useApi();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", default_price: 0, active: true, sort_order: 0 });

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/service-catalog?include_inactive=true");
      setItems(Array.isArray(data) ? data : []);
    } catch (err) { toast.error("Failed to load service catalog"); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const resetForm = () => {
    setForm({ name: "", description: "", default_price: 0, active: true, sort_order: items.length });
    setEditing(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, default_price: Number(form.default_price) || 0, sort_order: Number(form.sort_order) || 0 };
      if (editing) {
        await api.put(`/admin/service-catalog/${editing.id}`, payload);
        toast.success("Service updated");
      } else {
        await api.post("/admin/service-catalog", payload);
        toast.success("Service created");
      }
      resetForm();
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
    }
  };

  const handleEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name || "",
      description: item.description || "",
      default_price: Number(item.default_price) || 0,
      active: item.active !== false,
      sort_order: Number(item.sort_order) || 0,
    });
    setShowForm(true);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete or deactivate "${item.name}"?\n\nIf any client has it contracted, it will be deactivated (kept for history). Otherwise it will be permanently removed.`)) return;
    try {
      const { data } = await api.delete(`/admin/service-catalog/${item.id}`);
      toast.success(data?.deactivated ? "Service deactivated" : "Service deleted");
      fetchItems();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  const toggleActive = async (item) => {
    try {
      await api.put(`/admin/service-catalog/${item.id}`, { active: !item.active });
      fetchItems();
    } catch (err) { toast.error("Failed to update"); }
  };

  return (
    <div data-testid="service-catalog-panel" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-forest-50 rounded-lg flex items-center justify-center">
            <Briefcase className="w-5 h-5 text-forest-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-stone-800">Service Catalog</h2>
            <p className="text-sm text-stone-500">{items.length} service{items.length !== 1 ? "s" : ""} — used in Client cadastro and Service Order pre-fill</p>
          </div>
        </div>
        <button data-testid="add-catalog-btn" onClick={() => { resetForm(); setShowForm(true); }}
          className="flex items-center gap-2 bg-forest-600 hover:bg-forest-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
          <Plus className="w-4 h-4" /> New Service
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl border border-stone-200 p-6 shadow-sm" data-testid="catalog-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-stone-800">{editing ? "Edit Service" : "New Service"}</h3>
            <button onClick={resetForm} className="text-stone-400 hover:text-stone-600"><X className="w-5 h-5" /></button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-stone-700 mb-1">Service Name *</label>
                <input
                  data-testid="catalog-name-input"
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  required minLength={2} maxLength={120}
                  placeholder="e.g. Lawn Maintenance"
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Default Price (USD)</label>
                <div className="relative">
                  <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <input
                    data-testid="catalog-price-input"
                    type="number" min="0" step="0.01"
                    value={form.default_price}
                    onChange={e => setForm({ ...form, default_price: e.target.value })}
                    className="w-full pl-8 pr-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500"
                  />
                </div>
                <p className="text-[11px] text-stone-400 mt-1">Set to 0 for project-based quotes.</p>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Description</label>
              <textarea
                data-testid="catalog-desc-input"
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                rows={2} maxLength={400}
                placeholder="Short description shown next to the checkbox..."
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500 resize-none"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Sort Order</label>
                <input
                  type="number"
                  value={form.sort_order}
                  onChange={e => setForm({ ...form, sort_order: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500"
                />
                <p className="text-[11px] text-stone-400 mt-1">Lower numbers appear first.</p>
              </div>
              <div className="flex items-center gap-3 pt-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    data-testid="catalog-active-toggle"
                    type="checkbox"
                    checked={form.active}
                    onChange={e => setForm({ ...form, active: e.target.checked })}
                    className="accent-forest-600 w-4 h-4"
                  />
                  <span className="text-sm text-stone-700">Active (visible on cadastro)</span>
                </label>
              </div>
            </div>
            <div className="flex gap-3 pt-1">
              <button data-testid="catalog-submit-btn" type="submit"
                className="flex items-center gap-2 bg-forest-600 hover:bg-forest-700 text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors">
                <Check className="w-4 h-4" />{editing ? "Update" : "Create"}
              </button>
              <button type="button" onClick={resetForm}
                className="px-5 py-2 rounded-lg text-sm border border-stone-300 text-stone-600 hover:bg-stone-50 transition-colors">Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-xl border border-stone-200 overflow-hidden shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center h-32 text-stone-500">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="catalog-table">
              <thead>
                <tr className="border-b border-stone-200 bg-stone-50">
                  <th className="text-left px-4 py-3 font-medium text-stone-600">Service</th>
                  <th className="text-left px-4 py-3 font-medium text-stone-600 hidden md:table-cell">Description</th>
                  <th className="text-right px-4 py-3 font-medium text-stone-600">Default Price</th>
                  <th className="text-left px-4 py-3 font-medium text-stone-600 hidden lg:table-cell">Order</th>
                  <th className="text-center px-4 py-3 font-medium text-stone-600">Active</th>
                  <th className="text-right px-4 py-3 font-medium text-stone-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-stone-400">No services yet — add the first one.</td></tr>
                ) : items.map(item => (
                  <tr key={item.id} className="border-b border-stone-100 hover:bg-stone-50">
                    <td className="px-4 py-3 font-medium text-stone-800">{item.name}</td>
                    <td className="px-4 py-3 text-stone-500 hidden md:table-cell max-w-md truncate" title={item.description}>{item.description || "—"}</td>
                    <td className="px-4 py-3 text-right text-stone-700">{formatUSD(item.default_price || 0)}</td>
                    <td className="px-4 py-3 text-stone-500 hidden lg:table-cell">{item.sort_order ?? 0}</td>
                    <td className="px-4 py-3 text-center">
                      <button
                        data-testid={`catalog-active-${item.id}`}
                        onClick={() => toggleActive(item)}
                        className={`text-xs font-medium px-2 py-0.5 rounded-full ${item.active ? "bg-green-50 text-green-700" : "bg-stone-100 text-stone-500"}`}
                        title={item.active ? "Click to deactivate" : "Click to activate"}
                      >
                        {item.active ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => handleEdit(item)} data-testid={`catalog-edit-${item.id}`} className="p-1.5 rounded-md hover:bg-stone-100 text-stone-500 hover:text-forest-600"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => handleDelete(item)} data-testid={`catalog-delete-${item.id}`} className="p-1.5 rounded-md hover:bg-red-50 text-stone-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ========== Main Admin Module ==========
export default function AdminModule() {
  const api = useApi();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", password: "", role: "operador",
    professional_title: "", phone: "", profile_photo: "",
    drivers_license: "", emergency_contact_name: "", emergency_contact_phone: "",
  });
  const [formPerms, setFormPerms] = useState(emptyPerms());
  const [roleDefaults, setRoleDefaults] = useState({});
  const [activeSubTab, setActiveSubTab] = useState("users");
  const [showPassword, setShowPassword] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const photoInputRef = useRef(null);

  useEffect(() => {
    fetchUsers();
    fetchRoleDefaults();
  }, []);

  const fetchUsers = async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (err) { toast.error("Failed to load users"); }
    finally { setLoading(false); }
  };

  const fetchRoleDefaults = async () => {
    try {
      const { data } = await api.get("/admin/role-defaults");
      setRoleDefaults(data);
    } catch (err) { console.warn("Failed to load role defaults:", err.message); }
  };

  const resetForm = () => {
    setForm({
      name: "", email: "", password: "", role: "operador",
      professional_title: "", phone: "", profile_photo: "",
      drivers_license: "", emergency_contact_name: "", emergency_contact_phone: "",
    });
    setFormPerms(roleDefaults["operador"] || emptyPerms());
    setEditingUser(null);
    setShowForm(false);
    setShowPassword(false);
  };

  const handleRoleChange = (role) => {
    setForm(f => ({ ...f, role }));
    if (role !== "admin" && roleDefaults[role]) {
      setFormPerms(roleDefaults[role]);
    } else if (role === "admin") {
      setFormPerms(roleDefaults["admin"] || emptyPerms());
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Photo too large. Max 2 MB.");
      return;
    }
    setPhotoUploading(true);
    const reader = new FileReader();
    reader.onload = () => {
      setForm(f => ({ ...f, profile_photo: reader.result }));
      setPhotoUploading(false);
    };
    reader.onerror = () => { toast.error("Failed to read photo"); setPhotoUploading(false); };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: form.name, email: form.email, role: form.role,
        permissions: form.role !== "admin" ? formPerms : undefined,
        professional_title: form.professional_title,
        phone: form.phone,
        profile_photo: form.profile_photo,
        drivers_license: form.drivers_license,
        emergency_contact_name: form.emergency_contact_name,
        emergency_contact_phone: form.emergency_contact_phone,
      };
      if (editingUser) {
        if (form.password) payload.password = form.password;
        await api.put(`/admin/users/${editingUser._id}`, payload);
        toast.success("User updated");
      } else {
        payload.password = form.password;
        await api.post("/admin/users", payload);
        toast.success("User created");
      }
      resetForm();
      fetchUsers();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Operation failed");
    }
  };

  const handleEdit = (u) => {
    setEditingUser(u);
    setForm({
      name: u.name || "", email: u.email || "", password: "", role: u.role || "operador",
      professional_title: u.professional_title || "",
      phone: u.phone || "",
      profile_photo: u.profile_photo || "",
      drivers_license: u.drivers_license || "",
      emergency_contact_name: u.emergency_contact_name || "",
      emergency_contact_phone: u.emergency_contact_phone || "",
    });
    setFormPerms(u.permissions || roleDefaults[u.role] || emptyPerms());
    setShowForm(true);
    setShowPassword(false);
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete user "${u.name}"?`)) return;
    try {
      await api.delete(`/admin/users/${u._id}`);
      toast.success("User deleted");
      fetchUsers();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Cannot delete user");
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-stone-500">Loading...</div>;

  return (
    <div data-testid="admin-module" className="space-y-6">
      {/* Sub-tabs */}
      <div className="flex gap-2 border-b border-stone-200 pb-0 flex-wrap">
        <button
          data-testid="subtab-users"
          onClick={() => setActiveSubTab("users")}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
            activeSubTab === "users" ? "border-forest-600 text-forest-700" : "border-transparent text-stone-500 hover:text-stone-700"
          }`}
        >
          <span className="flex items-center gap-2"><Users className="w-4 h-4" /> Users & Permissions</span>
        </button>
        <button
          data-testid="subtab-catalog"
          onClick={() => setActiveSubTab("catalog")}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
            activeSubTab === "catalog" ? "border-forest-600 text-forest-700" : "border-transparent text-stone-500 hover:text-stone-700"
          }`}
        >
          <span className="flex items-center gap-2"><Briefcase className="w-4 h-4" /> Service Catalog</span>
        </button>
        <button
          data-testid="subtab-logs"
          onClick={() => setActiveSubTab("logs")}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
            activeSubTab === "logs" ? "border-forest-600 text-forest-700" : "border-transparent text-stone-500 hover:text-stone-700"
          }`}
        >
          <span className="flex items-center gap-2"><ScrollText className="w-4 h-4" /> Activity Logs</span>
        </button>
      </div>

      {activeSubTab === "logs" ? (
        <ActivityLogs />
      ) : activeSubTab === "catalog" ? (
        <ServiceCatalogPanel />
      ) : (
        <>
          {/* Header */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-forest-50 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-forest-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-stone-800">User Management</h2>
                <p className="text-sm text-stone-500">{users.length} user{users.length !== 1 ? "s" : ""} registered</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="download-backup-btn"
                onClick={async () => {
                  try {
                    const { data } = await api.get("/admin/backup/full");
                    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    const today = new Date().toISOString().slice(0, 10);
                    a.download = `gow-backup-${today}.json`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    setTimeout(() => URL.revokeObjectURL(url), 2000);
                    toast.success("Backup downloaded — save it to Google Drive");
                  } catch (err) { toast.error(err.response?.data?.detail || "Backup failed"); }
                }}
                className="flex items-center gap-2 border border-amber-300 text-amber-700 hover:bg-amber-50 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                title="Download full JSON backup of all business data — save it weekly to your own storage"
              >
                <ScrollText className="w-4 h-4" /> Download Backup
              </button>
              <button
                data-testid="add-user-btn"
                onClick={() => { resetForm(); setShowForm(true); }}
                className="flex items-center gap-2 bg-forest-600 hover:bg-forest-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                <Plus className="w-4 h-4" /> New User
              </button>
            </div>
          </div>

          {/* Form Modal */}
          {showForm && (
            <div
              className="fixed inset-0 z-50 flex items-start justify-center bg-stone-900/50 backdrop-blur-sm p-4 overflow-y-auto"
              onClick={resetForm}
            >
              <div
                className="bg-white rounded-xl shadow-2xl w-full max-w-3xl my-6 flex flex-col max-h-[calc(100vh-3rem)]"
                data-testid="user-form"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Sticky header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200 sticky top-0 bg-white rounded-t-xl z-10">
                  <h3 className="font-semibold text-stone-800">{editingUser ? "Edit User" : "New User"}</h3>
                  <button onClick={resetForm} data-testid="user-form-close" className="text-stone-400 hover:text-stone-600"><X className="w-5 h-5" /></button>
                </div>

                {/* Scrollable body */}
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
                  {/* Section: Account credentials */}
                  <section className="space-y-4">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-stone-500">Account Credentials</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-1">Name</label>
                        <input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                          className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500 focus:border-forest-500" required />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-1">Email</label>
                        <input data-testid="user-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                          className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500 focus:border-forest-500" required />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-1">
                          Password {editingUser && <span className="text-stone-400 font-normal">(leave blank to keep)</span>}
                        </label>
                        <div className="relative">
                          <input
                            data-testid="user-password-input"
                            type={showPassword ? "text" : "password"}
                            value={form.password}
                            onChange={(e) => setForm({ ...form, password: e.target.value })}
                            className="w-full px-3 py-2 pr-10 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500 focus:border-forest-500"
                            {...(!editingUser ? { required: true, minLength: 6 } : {})}
                          />
                          <button
                            type="button"
                            data-testid="toggle-password-visibility"
                            onClick={() => setShowPassword(s => !s)}
                            className="absolute inset-y-0 right-0 flex items-center px-3 text-stone-400 hover:text-stone-700"
                            title={showPassword ? "Hide password" : "Show password"}
                            aria-label="Toggle password visibility"
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-stone-700 mb-1">Professional Title</label>
                        <input
                          data-testid="user-professional-title-input"
                          value={form.professional_title}
                          onChange={(e) => setForm({ ...form, professional_title: e.target.value })}
                          placeholder="e.g., Crew Leader, Sales Manager"
                          className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500 focus:border-forest-500"
                        />
                        <p className="text-[11px] text-stone-400 mt-1">Informational label only — permissions are set via toggles below.</p>
                      </div>
                    </div>
                  </section>

                  {/* Section: Field operator profile */}
                  <section className="space-y-4 pt-2 border-t border-stone-100">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-stone-500">Field Operator Profile</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
                      {/* Profile Photo */}
                      <div className="md:col-span-1">
                        <label className="block text-sm font-medium text-stone-700 mb-1">Profile Photo</label>
                        <div className="flex items-center gap-3">
                          <div className="w-20 h-20 rounded-full overflow-hidden bg-stone-100 border border-stone-200 flex items-center justify-center flex-shrink-0">
                            {form.profile_photo ? (
                              <img src={form.profile_photo} alt="Preview" className="w-full h-full object-cover" />
                            ) : (
                              <User className="w-8 h-8 text-stone-400" />
                            )}
                          </div>
                          <div className="flex flex-col gap-1.5">
                            <input
                              ref={photoInputRef}
                              type="file"
                              accept="image/*"
                              onChange={handlePhotoChange}
                              className="hidden"
                              data-testid="user-photo-input"
                            />
                            <button
                              type="button"
                              onClick={() => photoInputRef.current?.click()}
                              disabled={photoUploading}
                              className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 border border-stone-300 rounded-lg text-stone-700 hover:bg-stone-50"
                              data-testid="user-photo-upload-btn"
                            >
                              <Camera className="w-3.5 h-3.5" /> {photoUploading ? "Loading…" : "Upload"}
                            </button>
                            {form.profile_photo && (
                              <button
                                type="button"
                                onClick={() => setForm(f => ({ ...f, profile_photo: "" }))}
                                className="text-[11px] text-red-500 hover:underline text-left"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-stone-700 mb-1">Phone Number</label>
                          <input
                            data-testid="user-phone-input"
                            value={form.phone}
                            onChange={(e) => setForm({ ...form, phone: e.target.value })}
                            placeholder="(555) 555-5555"
                            className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500 focus:border-forest-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-stone-700 mb-1">Driver's License</label>
                          <input
                            data-testid="user-drivers-license-input"
                            value={form.drivers_license}
                            onChange={(e) => setForm({ ...form, drivers_license: e.target.value })}
                            placeholder="License number"
                            className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500 focus:border-forest-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-stone-700 mb-1">Emergency Contact Name</label>
                          <input
                            data-testid="user-emergency-name-input"
                            value={form.emergency_contact_name}
                            onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })}
                            placeholder="Full name"
                            className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500 focus:border-forest-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-stone-700 mb-1">Emergency Contact Phone</label>
                          <input
                            data-testid="user-emergency-phone-input"
                            value={form.emergency_contact_phone}
                            onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })}
                            placeholder="(555) 555-5555"
                            className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500 focus:border-forest-500"
                          />
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Permissions */}
                  <section className="pt-2 border-t border-stone-100">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-stone-700">Module Permissions</label>
                      {form.role === "admin" && (
                        <span className="text-xs text-amber-600 font-medium bg-amber-50 px-2 py-0.5 rounded-md">Admin has full access</span>
                      )}
                    </div>
                    <PermissionMatrix
                      permissions={formPerms}
                      onChange={setFormPerms}
                      disabled={form.role === "admin"}
                    />
                  </section>
                </form>

                {/* Sticky footer */}
                <div className="flex gap-3 px-6 py-4 border-t border-stone-200 sticky bottom-0 bg-white rounded-b-xl">
                  <button
                    data-testid="user-submit-btn"
                    type="button"
                    onClick={handleSubmit}
                    className="flex items-center gap-2 bg-forest-600 hover:bg-forest-700 text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    <Check className="w-4 h-4" /> {editingUser ? "Update" : "Create"}
                  </button>
                  <button type="button" onClick={resetForm}
                    className="px-5 py-2 rounded-lg text-sm border border-stone-300 text-stone-600 hover:bg-stone-50">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Users Table */}
          <div className="bg-white rounded-xl border border-stone-200 overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="users-table">
                <thead>
                  <tr className="border-b border-stone-200 bg-stone-50">
                    <th className="text-left px-4 py-3 font-medium text-stone-600">Name</th>
                    <th className="text-left px-4 py-3 font-medium text-stone-600">Email</th>
                    <th className="text-left px-4 py-3 font-medium text-stone-600">Title / Role</th>
                    <th className="text-left px-4 py-3 font-medium text-stone-600 hidden lg:table-cell">Permissions</th>
                    <th className="text-right px-4 py-3 font-medium text-stone-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => {
                    const perms = u.permissions || {};
                    const viewCount = MODULES.filter(m => perms[m.key]?.view).length;
                    const editCount = MODULES.filter(m => perms[m.key]?.edit).length;
                    return (
                      <tr key={u._id} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`user-row-${u._id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-stone-100 overflow-hidden flex items-center justify-center flex-shrink-0">
                              {u.profile_photo ? (
                                <img src={u.profile_photo} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <User className="w-4 h-4 text-stone-400" />
                              )}
                            </div>
                            <span className="font-medium text-stone-800">{u.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-stone-600">{u.email}</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-0.5">
                            {u.professional_title && (
                              <span className="text-sm text-stone-700 font-medium">{u.professional_title}</span>
                            )}
                            <span className={`inline-flex w-fit items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                              u.role === "admin" ? "bg-amber-50 text-amber-700" :
                              u.role === "operador_gerente" ? "bg-purple-50 text-purple-700" :
                              u.role === "financeiro" ? "bg-emerald-50 text-emerald-700" :
                              "bg-blue-50 text-blue-700"
                            }`}>
                              {u.role === "admin" ? <Shield className="w-3 h-3" /> : <User className="w-3 h-3" />}
                              {ROLES.find(r => r.value === u.role)?.label || u.role}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <div className="flex gap-2 text-xs">
                            <span className="bg-forest-50 text-forest-700 px-1.5 py-0.5 rounded">{viewCount} view</span>
                            <span className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">{editCount} edit</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => handleEdit(u)} className="p-1.5 rounded-md hover:bg-stone-100 text-stone-500 hover:text-forest-600" title="Edit">
                              <Pencil className="w-4 h-4" />
                            </button>
                            {u._id !== currentUser?.id && (
                              <button onClick={() => handleDelete(u)} className="p-1.5 rounded-md hover:bg-red-50 text-stone-500 hover:text-red-600" title="Delete">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
